import { Navigate, useRoutes } from 'react-router-dom'
import AuthGuard from '../auth/AuthGuard'
import GuestGuard from '../auth/GuestGuard'
import CompactLayout from '../layouts/compact'
import DashboardLayout from '../layouts/dashboard'

import { useMemo } from 'react'
import { useAuthContext } from '../auth/useAuthContext'
import {
  LoginPage,
  NewPasswordPage,
  ResetPasswordPage,
  OperatorLoginPage,
  TrackEventsPage,
  TrackCategoriesPage,
  TrackPanelPage,
  TrackPublicPanelPage,
  TrackAuditPage,
  TrackReportsPage,
  PublicEventsPage,
  PublicCategoriesPage,
  Page500,
  Page403,
  Page404,
  ComingSoonPage,
  MaintenancePage,
} from './elements'

export default function Router() {
  const { user, isOperator } = useAuthContext()

  const dashboard = useMemo(() => {
    if (isOperator && user?.event_id) {
      return `/dashboard/track/events/${user.event_id}/categories`
    }
    return '/dashboard/track/events'
  }, [user?.event_id, isOperator])

  return useRoutes([
    // Auth
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'operator/login',
          element: (
            <GuestGuard>
              <OperatorLoginPage />
            </GuestGuard>
          ),
        },
        {
          element: <CompactLayout />,
        },
      ],
    },

    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={dashboard} replace />,
          index: true,
        },
        {
          path: 'track',
          children: [
            { path: 'events', element: <TrackEventsPage /> },
            { path: 'events/:eventId/categories', element: <TrackCategoriesPage /> },
            { path: 'events/:eventId/reports', element: <TrackReportsPage /> },
            { path: 'panel/:categoryId', element: <TrackPanelPage /> },
            { path: 'audit/:ticketId', element: <TrackAuditPage /> },
          ],
        },
      ],
    },

    // Public Spectator Panel & Routes
    {
      path: 'track/public',
      children: [{ path: 'panel/:categoryId', element: <TrackPublicPanelPage /> }],
    },
    {
      path: 'public',
      children: [{ path: 'events/:eventId/categories', element: <PublicCategoriesPage /> }],
    },

    // Main Routes
    {
      children: [
        {
          element: <PublicEventsPage />,
          index: true,
        },
        {
          element: <CompactLayout />,
          children: [
            { path: 'forgot-password', element: <ResetPasswordPage /> },
            { path: 'reset-password', element: <NewPasswordPage /> },
          ],
        },
      ],
    },
    {
      element: <CompactLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoonPage /> },
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ])
}
